'use client';

import { Box, Typography } from '@mui/material';
import { useSequencingContext } from '@repo/sequencing/client/context/SequencingContext';
import { getSortedSummaryTableRows } from '@repo/sequencing/utils';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import type { SummaryTableRowType } from '@repo/supergraph/roundup/base/types';
import { snakeCase } from 'lodash';
import { createContext, useContext, useMemo, useRef, type RefObject } from 'react';
import { DesktopFilters } from '../components/OffersFilter/DesktopFilterBar';
import { DesktopFiltersWithGeo } from '../components/OffersFilter/DesktopFilterBar/DesktopFiltersWithGeo';
import { MobileFilters, MobileFiltersWithGeo } from '../components/OffersFilter/MobileFilters';
import { OffersFilterStoreProvider } from '../components/OffersFilter/store';
import type { FilterEnabledSlugs } from '../components/OffersFilter/store/types';

type FilterRefContextType = {
  filteredProductsRef: RefObject<HTMLDivElement | null>;
};

const FilterRefContext = createContext<FilterRefContextType | null>(null);

function useFilterRef() {
  const context = useContext(FilterRefContext);
  if (!context) {
    throw new Error('useFilterRef must be used within FiltersInHeaderProvider');
  }
  return context;
}

type ProviderProps = {
  rows: SummaryTableRowType[];
  slug: string;
  children: React.ReactNode;
};

export function FiltersInHeaderProvider({ rows, slug, children }: ProviderProps) {
  const filteredProductsRef = useRef<HTMLDivElement>(null);
  const { sequence } = useSequencingContext();

  // Pre-sort rows by summaryTable sequence so store consumers receive them in the correct
  // order. Product card ordering is separate — see getSortedProductCardSections.
  const sortedRows = useMemo(
    () => getSortedSummaryTableRows(rows, sequence.summaryTable),
    [sequence.summaryTable],
  );

  return (
    <OffersFilterStoreProvider offers={sortedRows} slug={slug as FilterEnabledSlugs}>
      <FilterRefContext.Provider value={{ filteredProductsRef }}>
        {children}
      </FilterRefContext.Provider>
    </OffersFilterStoreProvider>
  );
}

type FilterDisplayProps = {
  slug: string;
  initialLocation?: StateAbbreviation;
};

export function FiltersInHeaderDisplay({ slug, initialLocation }: FilterDisplayProps) {
  const { filteredProductsRef } = useFilterRef();
  const cardRef = useRef<HTMLDivElement>(null);
  const snakeCasedSlug = snakeCase(slug);
  const analyticsProps = {
    actionSection: `banking_${snakeCasedSlug}_filter`,
    pageIdentifier: snakeCasedSlug,
  };

  return (
    <Box
      ref={cardRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '1rem', md: '1.5rem' },
        backgroundColor: 'common.white',
        borderRadius: 3,
        paddingX: { xs: '1rem', md: '2rem' },
        paddingTop: { xs: '1.5rem', md: '2rem' },
        paddingBottom: { xs: '1rem', md: '2rem' },
        boxShadow: '0 1px 4px 0 rgba(100, 102, 106, 0.36), 0 4px 8px 0 rgba(100, 102, 106, 0.10)',
      }}
    >
      <Typography variant="textBold" sx={{ color: 'neutral.dark' }}>
        PERSONALIZE YOUR SEARCH
      </Typography>
      {initialLocation ? (
        <DesktopFiltersWithGeo
          mainSectionRef={filteredProductsRef}
          slug={slug}
          initialLocation={initialLocation}
          analyticsProps={analyticsProps}
          isFiltersInHeader
          scrollTargetRef={cardRef}
        />
      ) : (
        <DesktopFilters
          mainSectionRef={filteredProductsRef}
          slug={slug}
          analyticsProps={analyticsProps}
          isFiltersInHeader
          scrollTargetRef={cardRef}
        />
      )}
      {initialLocation ? (
        <MobileFiltersWithGeo
          slug={slug}
          analyticsProps={analyticsProps}
          initialLocation={initialLocation}
          isFiltersInHeader
        />
      ) : (
        <MobileFilters
          slug={slug}
          initialLocation={initialLocation}
          analyticsProps={analyticsProps}
          isFiltersInHeader
        />
      )}
    </Box>
  );
}

type FilterTargetProps = {
  children: React.ReactNode;
};

export function FiltersInHeaderTarget({ children }: FilterTargetProps) {
  const { filteredProductsRef } = useFilterRef();

  return <div ref={filteredProductsRef}>{children}</div>;
}
